import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./RechercheModal.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ficheLbl = { fontWeight: 700, color: "var(--primary-blue, #003399)", fontSize: 11, textTransform: "uppercase" };

export default function AvocatsModal({ onClose, onSelectDossier }) {
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [code, setCode] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // avocat sélectionné
  const [dossiers, setDossiers] = useState(null); // {mandats, attestations, refus}
  const [dossiersLoading, setDossiersLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("mandats");
  const nomRef = useRef(null);

  useEffect(() => { if (nomRef.current) nomRef.current.focus(); }, []);

  const handleSearch = async () => {
    setError("");
    setSelected(null);
    setDossiers(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/avocats/recherche`, {
        params: { nom: nom.trim() || undefined, prenom: prenom.trim() || undefined, code: code.trim() || undefined },
      });
      setResults(res.data);
      if (res.data.length === 0) setError("Aucun avocat trouvé.");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAvocat = async (av) => {
    setSelected(av);
    setDetailTab("mandats");
    setDossiers(null);
    setDossiersLoading(true);
    try {
      const res = await axios.get(`${API}/avocats/${encodeURIComponent(av.code)}/dossiers`);
      setDossiers(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDossiersLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) { e.preventDefault(); handleSearch(); }
  };

  const handleOpenDossier = (item) => {
    if (!onSelectDossier) return;
    if (!item.no_client) return;
    onSelectDossier(item);
  };

  const counts = dossiers ? {
    mandats: dossiers.mandats.length,
    attestations: dossiers.attestations.length,
    refus: dossiers.refus.length,
  } : { mandats: 0, attestations: 0, refus: 0 };

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setDetailTab(id)}
      data-testid={`avocat-detail-tab-${id}`}
      style={{
        padding: "5px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
        border: "1px solid var(--primary-blue, #003399)", borderBottom: "none",
        borderRadius: "6px 6px 0 0",
        background: detailTab === id ? "var(--primary-blue, #003399)" : "#e4e9f0",
        color: detailTab === id ? "#fff" : "var(--primary-blue, #003399)",
      }}
    >
      {label} ({counts[id]})
    </button>
  );

  return (
    <div className="rech-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rech-modal" data-testid="avocats-modal" style={{ maxWidth: 1250 }}>
        <div className="rech-titlebar">
          <span>Avocats — recherche et dossiers confiés</span>
          <button className="rech-close" onClick={onClose} data-testid="avocats-close" title="Fermer">×</button>
        </div>

        <div className="rech-body" style={{ overflowY: "hidden" }}>
          {/* Critères */}
          <div className="rech-criteres">
            <div className="rech-cols">
              <div className="rech-col">
                <div className="rech-field">
                  <label>Nom</label>
                  <input ref={nomRef} value={nom} onChange={e => setNom(e.target.value)}
                         onKeyDown={handleKeyDown} data-testid="avocats-nom" />
                </div>
              </div>
              <div className="rech-col">
                <div className="rech-field">
                  <label>Prénom</label>
                  <input value={prenom} onChange={e => setPrenom(e.target.value)}
                         onKeyDown={handleKeyDown} data-testid="avocats-prenom" />
                </div>
              </div>
              <div className="rech-col">
                <div className="rech-field">
                  <label>Code avocat</label>
                  <input value={code} onChange={e => setCode(e.target.value)}
                         onKeyDown={handleKeyDown} maxLength={6} data-testid="avocats-code" />
                </div>
              </div>
            </div>
            <div className="rech-actions">
              <button className="rech-btn-primary" onClick={handleSearch} disabled={loading}
                      data-testid="avocats-btn-search">
                {loading ? "Recherche…" : "Rechercher"}
              </button>
              <button className="rech-btn-secondary" data-testid="avocats-btn-reset"
                      onClick={() => { setNom(""); setPrenom(""); setCode(""); setResults(null); setSelected(null); setDossiers(null); setError(""); }}>
                Initialiser
              </button>
              <button className="rech-btn-secondary" onClick={onClose} data-testid="avocats-btn-close">Fermer</button>
              {error && <span className="rech-error" data-testid="avocats-error">{error}</span>}
            </div>
          </div>

          {/* Résultats avocats */}
          {results !== null && results.length > 0 && (
            <div className="rech-results">
              <div className="rech-results-header">
                <span data-testid="avocats-count">
                  <strong>{results.length}</strong> avocat{results.length > 1 ? "s" : ""} trouvé{results.length > 1 ? "s" : ""}
                </span>
                <span className="rech-hint">Cliquez sur un avocat pour voir ses mandats, attestations et refus</span>
              </div>
              {/* Hauteur fixe = 5 lignes (30px entête + 5 × 28px) — ascenseur au-delà */}
              <div className="rech-table-wrap" style={{ height: 170 }}>
                <table className="rech-table">
                  <thead>
                    <tr>
                      <th>Code</th><th>Nom</th><th>Prénom</th><th>Étude</th><th>Ville</th><th>Téléphone</th><th>Actif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((a, i) => (
                      <tr key={a.code}
                          className={selected?.code === a.code ? "rech-row-selected" : ""}
                          onClick={() => handleSelectAvocat(a)}
                          style={{ cursor: "pointer" }}
                          data-testid={`avocats-row-${i}`}>
                        <td>{a.code}</td>
                        <td>{a.nom}</td>
                        <td>{a.prenom}</td>
                        <td>{a.etude}</td>
                        <td>{a.ville}</td>
                        <td>{a.tel}</td>
                        <td>{a.actif ? "Oui" : "Non"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fiche + détail avocat sélectionné */}
          {selected && (
            <div style={{ marginTop: 10 }} data-testid="avocat-detail">
              {/* Fiche de l'avocat : adresse civique + courriel */}
              <fieldset className="win-groupbox compact" style={{ marginBottom: 10 }} data-testid="avocat-fiche">
                <legend>Fiche de l'avocat</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 24px', fontSize: 13 }}>
                  <div><span style={ficheLbl}>Nom : </span>{selected.nom}, {selected.prenom}</div>
                  <div><span style={ficheLbl}>Code : </span>{selected.code}</div>
                  <div><span style={ficheLbl}>Étude : </span>{selected.etude || "—"}</div>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <span style={ficheLbl}>Adresse : </span>
                    <span data-testid="avocat-fiche-adresse">
                      {[selected.rue, selected.ville, selected.province, selected.code_postal].filter(Boolean).join(", ") || "—"}
                    </span>
                  </div>
                  <div>
                    <span style={ficheLbl}>Courriel : </span>
                    <span data-testid="avocat-fiche-courriel">
                      {selected.courriel ? <a href={`mailto:${selected.courriel}`}>{selected.courriel}</a> : "—"}
                    </span>
                  </div>
                  <div><span style={ficheLbl}>Téléphone : </span>{selected.tel || "—"}</div>
                  <div><span style={ficheLbl}>Télécopieur : </span>{selected.fax || "—"}</div>
                  <div><span style={ficheLbl}>Actif : </span>{selected.actif ? "Oui" : "Non"}</div>
                </div>
              </fieldset>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                Dossiers confiés
                {dossiersLoading && <span style={{ fontWeight: 400, marginLeft: 10, color: "#666" }}>Chargement…</span>}
                {dossiers && !dossiersLoading && (
                  <span style={{ fontWeight: 400, marginLeft: 10, color: "#666", fontStyle: "italic", fontSize: 11 }}>
                    Double-cliquez sur une ligne pour ouvrir le dossier
                  </span>
                )}
              </div>
              {dossiers && (
                <>
                  <div style={{ display: "flex", gap: 4, borderBottom: "2px solid var(--primary-blue, #003399)" }}>
                    {tabBtn("mandats", "Mandats confiés")}
                    {tabBtn("attestations", "Attestations")}
                    {tabBtn("refus", "Refus")}
                  </div>
                  {/* Hauteur fixe = 5 lignes — ascenseur au-delà */}
                  <div className="rech-table-wrap" style={{ height: 170, marginTop: 0 }}>
                    {detailTab === "mandats" && (
                      <table className="rech-table" data-testid="avocat-mandats-table">
                        <thead>
                          <tr><th>Requérant</th><th>No séq.</th><th>No réf.</th><th>No dossier</th><th>Date dem.</th><th>Nature</th><th>Statut</th><th>Rég.-Bur.</th></tr>
                        </thead>
                        <tbody>
                          {dossiers.mandats.length === 0 && (
                            <tr><td colSpan={8} style={{ fontStyle: "italic", color: "#666" }}>Aucun mandat confié.</td></tr>
                          )}
                          {dossiers.mandats.map((m, i) => (
                            <tr key={i} onDoubleClick={() => handleOpenDossier(m)}
                                style={{ cursor: "pointer" }}
                                title="Double-cliquez pour ouvrir le dossier"
                                data-testid={`avocat-mandat-row-${i}`}>
                              <td>{m.requerant}</td><td>{m.no_seq_daj}</td><td>{m.no_ref}</td>
                              <td>{m.no_dossier}</td><td>{m.date_demande}</td><td>{m.nature}</td>
                              <td>{m.statut}</td><td>{m.region_bureau}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {detailTab === "attestations" && (
                      <table className="rech-table" data-testid="avocat-attestations-table">
                        <thead>
                          <tr><th>Requérant</th><th>No réf.</th><th>No client</th><th>Date émise</th><th>Type</th><th>Nature</th><th>Rég.-Bur.</th></tr>
                        </thead>
                        <tbody>
                          {dossiers.attestations.length === 0 && (
                            <tr><td colSpan={7} style={{ fontStyle: "italic", color: "#666" }}>Aucune attestation.</td></tr>
                          )}
                          {dossiers.attestations.map((m, i) => (
                            <tr key={i} onDoubleClick={() => handleOpenDossier(m)}
                                style={{ cursor: "pointer" }}
                                title="Double-cliquez pour ouvrir le dossier"
                                data-testid={`avocat-attestation-row-${i}`}>
                              <td>{m.requerant}</td><td>{m.no_ref}</td><td>{m.no_client}</td>
                              <td>{m.date_emise}</td><td>{m.type}</td><td>{m.nature}</td><td>{m.region_bureau}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {detailTab === "refus" && (
                      <table className="rech-table" data-testid="avocat-refus-table">
                        <thead>
                          <tr><th>Requérant</th><th>No réf.</th><th>No client</th><th>Date émise</th><th>Date refus</th><th>Nature</th><th>Rég.-Bur.</th></tr>
                        </thead>
                        <tbody>
                          {dossiers.refus.length === 0 && (
                            <tr><td colSpan={7} style={{ fontStyle: "italic", color: "#666" }}>Aucun refus.</td></tr>
                          )}
                          {dossiers.refus.map((m, i) => (
                            <tr key={i} onDoubleClick={() => handleOpenDossier(m)}
                                style={{ cursor: "pointer" }}
                                title="Double-cliquez pour ouvrir le dossier"
                                data-testid={`avocat-refus-row-${i}`}>
                              <td>{m.requerant}</td><td>{m.no_ref}</td><td>{m.no_client}</td>
                              <td>{m.date_emise}</td><td>{m.date_refus}</td><td>{m.nature}</td><td>{m.region_bureau}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
